/**
 * ラリーの作成・編集フォーム
 *
 * シートとしても、地図を背景にした常設シートの 1 モードとしても使えるよう中身だけを持つ。
 */

import { Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { CreateRallyParams, StampRallySummary } from '@/api/types/stamp-rally'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { cn } from '@/lib/utils'

/** 地図マーカーにも使うので、背景の上で見分けがつく色に絞る */
const COLORS = ['#F97316', '#EF4444', '#22C55E', '#3B82F6', '#A855F7', '#EAB308']

type Props = {
  onSubmit: (params: CreateRallyParams) => Promise<void>
  /** 指定すると編集モードになる */
  rally?: StampRallySummary
  onDelete?: () => void
}

export const RallyFormPanel = ({ onSubmit, rally, onDelete }: Props) => {
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [officialUrl, setOfficialUrl] = useState('')
  const [color, setColor] = useState(COLORS[0])
  const { isSubmitting, run } = useSubmitLock()
  useEffect(() => {
    setTitle(rally?.title ?? '')
    setCategory(rally?.category ?? '')
    setDescription(rally?.description ?? '')
    setOfficialUrl(rally?.official_url ?? '')
    setColor(rally?.color ?? COLORS[0])
  }, [rally])

  const handleSubmit = async () => {
    if (!title.trim()) return
    await run(() =>
      onSubmit({
        title: title.trim(),
        category: category.trim() || null,
        description: description.trim() || null,
        official_url: officialUrl.trim() || null,
        color,
      }),
    )
  }

  return (
    <div className="space-y-4 px-4 pt-4 pb-6">
      <div className="space-y-1.5">
        <Label htmlFor="rally-title">タイトル</Label>
        <Input
          id="rally-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="日本100名城"
          maxLength={200}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="rally-category">カテゴリ</Label>
        <Input
          id="rally-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="城 / ピンバッジ / 鉄道 など"
          maxLength={50}
        />
        <p className="text-xs text-muted-foreground">一覧はここでセクション分けされます</p>
      </div>

      <div className="space-y-1.5">
        <Label>テーマカラー</Label>
        <div className="flex gap-2">
          {COLORS.map((value) => (
            <button
              key={value}
              onClick={() => setColor(value)}
              aria-label={`カラー ${value}`}
              className={cn(
                'size-8 rounded-full border-2 transition-transform',
                color === value ? 'scale-110 border-foreground' : 'border-transparent',
              )}
              style={{ backgroundColor: value }}
            />
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="rally-url">公式サイト</Label>
        <Input
          id="rally-url"
          value={officialUrl}
          onChange={(e) => setOfficialUrl(e.target.value)}
          placeholder="https://..."
          inputMode="url"
          maxLength={500}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="rally-description">メモ</Label>
        <Textarea
          id="rally-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>

      <Button onClick={handleSubmit} disabled={!title.trim() || isSubmitting} className="w-full">
        {rally ? '保存' : '作成'}
      </Button>

      {rally && onDelete && (
        <Button variant="ghost" onClick={onDelete} disabled={isSubmitting} className="w-full text-destructive">
          <Trash2 className="size-4" />
          このラリーを削除
        </Button>
      )}
    </div>
  )
}
